import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';

/**
 * Quick-add dedicated fragment client.
 * Fetches a minimal product fragment from `/products/<handle>?view=quick-add`
 * and handles variant switching + add-to-cart entirely client-side.
 */

const CUOTAS_COUNT = 12;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, string>} */
  #cachedHtml = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {Array|null} */
  #variants = null;
  /** @type {{format:string}|null} */
  #moneyConfig = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    // Support the custom "sos" (Completá tu outfit) carousel case where url is set via data-attr
    const dataUrl = this.dataset.productUrl;

    const href = productLink?.href || dataUrl;
    if (!href) return '';

    const url = new URL(href, window.location.origin);

    if (url.searchParams.has('variant')) return url.toString();

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
  }

  #handleCartUpdate = () => {
    this.#cachedHtml.clear();
  };

  /**
   * Entry: click on the quick-add "+" button
   */
  handleClick = async (event) => {
    event.preventDefault();

    const url = this.productPageUrl;
    if (!url) return;

    const fragmentUrl = this.#buildFragmentUrl(url);
    let html = this.#cachedHtml.get(fragmentUrl);

    if (!html) {
      html = await this.#fetchFragment(fragmentUrl);
      if (html) this.#cachedHtml.set(fragmentUrl, html);
    }

    if (!html) return;

    this.#renderInModal(html);
    this.#openQuickAddModal();
  };

  /**
   * @param {string} productUrl
   * @returns {string}
   */
  #buildFragmentUrl(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('view', 'quick-add');
    return url.toString();
  }

  /**
   * @param {string} url
   * @returns {Promise<string|null>}
   */
  async #fetchFragment(url) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Quick-add fetch failed: ${response.status}`);
      return await response.text();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return null;
      console.error('[quick-add]', err);
      return null;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @param {string} html
   */
  #renderInModal(html) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fragment = doc.querySelector('[data-qa-fragment]');
    if (!fragment) return;

    modalContent.innerHTML = '';
    modalContent.appendChild(fragment);

    // Parse embedded JSON data
    this.#variants = this.#readJsonById(fragment, '[data-qa-variants-json]') || [];
    this.#moneyConfig = this.#readJsonById(fragment, '[data-qa-money-config]') || { format: '${{amount}}' };

    // Wire interactions
    this.#wireOptionButtons(fragment);
    this.#wireAddToCart(fragment);
    this.#wireBuyNow(fragment);
  }

  /**
   * @param {Element} root
   * @param {string} selector
   */
  #readJsonById(root, selector) {
    const el = root.querySelector(selector);
    if (!el || !el.textContent) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  /**
   * @param {Element} fragment
   */
  #wireOptionButtons(fragment) {
    const buttons = fragment.querySelectorAll('[data-qa-option-value]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const position = btn.getAttribute('data-qa-position');
        const value = btn.getAttribute('data-qa-value');
        if (!position || !value) return;

        // Toggle selection within the same option group
        const group = btn.closest('[data-qa-option-wrap]');
        if (group) {
          group.querySelectorAll('[data-qa-option-value]').forEach((b) => {
            b.classList.remove('is-selected');
            b.setAttribute('aria-checked', 'false');
          });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-checked', 'true');

          const label = group.querySelector(`[data-qa-option-selected="${position}"]`);
          if (label) label.textContent = value;
        }

        this.#updateVariantFromSelection(fragment);
      });
    });
  }

  /**
   * Reads current selected values and finds the matching variant,
   * then updates price/stock/button state.
   * @param {Element} fragment
   */
  #updateVariantFromSelection(fragment) {
    if (!this.#variants || this.#variants.length === 0) return;

    const selectedByPos = {};
    fragment.querySelectorAll('[data-qa-option-wrap]').forEach((group) => {
      const position = group.getAttribute('data-qa-option-position');
      const selected = group.querySelector('[data-qa-option-value].is-selected');
      if (position && selected) {
        selectedByPos[position] = selected.getAttribute('data-qa-value');
      }
    });

    const match = this.#variants.find((v) => {
      if (selectedByPos['1'] && v.option1 !== selectedByPos['1']) return false;
      if (selectedByPos['2'] && v.option2 !== selectedByPos['2']) return false;
      if (selectedByPos['3'] && v.option3 !== selectedByPos['3']) return false;
      return true;
    });

    if (!match) return;

    // Hidden id
    const idInput = fragment.querySelector('[data-qa-variant-id]');
    if (idInput instanceof HTMLInputElement) idInput.value = String(match.id);

    // Price
    const priceEl = fragment.querySelector('[data-qa-price]');
    if (priceEl) priceEl.textContent = this.#formatMoney(match.price);

    const compareEl = fragment.querySelector('[data-qa-compare]');
    if (compareEl instanceof HTMLElement) {
      if (match.compare_at_price && match.compare_at_price > match.price) {
        compareEl.textContent = this.#formatMoney(match.compare_at_price);
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.setAttribute('hidden', '');
      }
    }

    // Installments
    const instEl = fragment.querySelector('[data-qa-installments]');
    if (instEl) {
      const cuota = Math.round(match.price / CUOTAS_COUNT);
      instEl.innerHTML = `${CUOTAS_COUNT} cuotas de <strong>${this.#formatMoney(cuota)}</strong> sin interés`;
    }

    // Stock message
    this.#updateStockDisplay(fragment, match);

    // Buttons
    const addBtn = fragment.querySelector('[data-qa-add-btn]');
    const buyBtn = fragment.querySelector('[data-qa-buy-btn]');
    const addText = fragment.querySelector('[data-qa-add-text]');
    const unavailable = !match.available;
    [addBtn, buyBtn].forEach((b) => {
      if (!(b instanceof HTMLButtonElement)) return;
      b.disabled = unavailable;
      b.toggleAttribute('aria-disabled', unavailable);
    });
    if (addText) addText.textContent = unavailable ? 'Agotado' : 'Agregar al carrito';
  }

  /**
   * @param {Element} fragment
   * @param {{available:boolean, inventory_management?:string|null, inventory_policy?:string|null, inventory_quantity?:number}} variant
   */
  #updateStockDisplay(fragment, variant) {
    const stock = fragment.querySelector('[data-qa-stock]');
    if (!stock) return;

    stock.innerHTML = '';
    const dot = document.createElement('span');
    const text = document.createElement('span');
    text.className = 'qa-fragment__stock-text';
    dot.className = 'qa-fragment__stock-dot';

    if (!variant.available) {
      dot.classList.add('qa-fragment__stock-dot--out');
      text.textContent = 'Agotado';
    } else if (
      variant.inventory_management === 'shopify' &&
      variant.inventory_policy === 'deny' &&
      typeof variant.inventory_quantity === 'number' &&
      variant.inventory_quantity > 0 &&
      variant.inventory_quantity <= 3
    ) {
      dot.classList.add('qa-fragment__stock-dot--low');
      text.textContent =
        variant.inventory_quantity === 1
          ? 'Solo queda 1 disponible, ordená pronto'
          : `Solo quedan ${variant.inventory_quantity} disponibles, ordená pronto`;
    } else {
      dot.classList.add('qa-fragment__stock-dot--in');
      text.textContent = 'Disponible';
    }

    stock.appendChild(dot);
    stock.appendChild(text);
  }

  /**
   * Minimal money formatter — handles Shopify's amount/amount_no_decimals patterns.
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const format = this.#moneyConfig?.format || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDec = Math.round(cents / 100).toString();
    const amountWithComma = amount.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.').replace(/(.*)\./, '$1,');
    // Simpler: build "X.XXX,XX" format (common in UY)
    const intPart = Math.floor(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const decPart = (cents % 100).toString().padStart(2, '0');
    const amountComma = `${intPart},${decPart}`;
    const amountCommaNoDec = intPart;

    return format
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountCommaNoDec)
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, `${intPart.replace(/\./g, ' ')},${decPart}`)
      .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, intPart.replace(/\./g, ' '))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, `${intPart.replace(/\./g, "'")},${decPart}`);
  }

  /**
   * @param {Element} fragment
   */
  #wireAddToCart(fragment) {
    const form = fragment.querySelector('[data-qa-form]');
    if (!(form instanceof HTMLFormElement)) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const fragmentRoot = /** @type {HTMLElement} */ (fragment);
      if (fragmentRoot.classList.contains('is-adding')) return;

      fragmentRoot.classList.add('is-adding', 'is-loading');

      try {
        const idInput = form.querySelector('[data-qa-variant-id]');
        if (!(idInput instanceof HTMLInputElement) || !idInput.value) throw new Error('No variant selected');

        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err?.description || err?.message || 'No se pudo agregar al carrito');
        }

        const item = await response.json();

        // Fire Horizon cart-update event so the drawer/mini-cart reacts
        const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = cartResponse.ok ? await cartResponse.json() : null;

        document.dispatchEvent(
          new CartUpdateEvent({
            resource: 'variant',
            resourceId: item?.variant_id,
            data: {
              source: 'quick-add',
              productId: item?.product_id,
              didError: false,
              itemCount: cart?.item_count,
            },
          })
        );
      } catch (err) {
        console.error('[quick-add] cart add failed', err);
        this.#showError(fragmentRoot, err instanceof Error ? err.message : 'Error al agregar al carrito');
      } finally {
        fragmentRoot.classList.remove('is-adding', 'is-loading');
      }
    });
  }

  /**
   * @param {Element} fragment
   */
  #wireBuyNow(fragment) {
    const btn = fragment.querySelector('[data-qa-buy-btn]');
    const form = fragment.querySelector('[data-qa-form]');
    if (!(btn instanceof HTMLButtonElement) || !(form instanceof HTMLFormElement)) return;

    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (btn.disabled) return;

      const idInput = form.querySelector('[data-qa-variant-id]');
      if (!(idInput instanceof HTMLInputElement) || !idInput.value) return;

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Cart add failed');
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[quick-add] buy-now failed', err);
        // Fallback: standard POST to /cart which redirects
        form.action = '/cart';
        form.submit();
      }
    });
  }

  /**
   * @param {HTMLElement} fragment
   * @param {string} message
   */
  #showError(fragment, message) {
    let errorEl = fragment.querySelector('.qa-fragment__error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'qa-fragment__error';
      const form = fragment.querySelector('[data-qa-form]');
      if (form) form.prepend(errorEl);
    }
    errorEl.textContent = message;
    errorEl.classList.add('is-visible');
    setTimeout(() => errorEl?.classList.remove('is-visible'), 4000);
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.toggleAttribute('stay-visible', true);
    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute('stay-visible', false),
      { once: true }
    );

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    dialogComponent.closeDialog();
  };
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  #handleCartUpdate = (/** @type {CartUpdateEvent} */ event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
